import { useState, useCallback, useMemo } from "react";
import BreakdownDialog from "./BreakdownDialog";
import { assetTypeValues } from "../../../constants";

export const FLEET_CASHFLOW_FORECAST_BREAKDOWN_COLUMNS = [
  { key: "lease_ref_code", label: "Lease Ref Code" },
  { key: "lessee", label: "Lessee" },
  { key: "asset_label", label: "Asset" },
  { key: "known_invoiced", label: "Known Invoiced", mono: true },
  { key: "projected_rent", label: "Projected Rent", mono: true },
];

function formatAmount(value, currency) {
  const amount = typeof value === "number" ? value : parseFloat(value);
  if (Number.isNaN(amount)) return "-";
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency || "USD",
      maximumFractionDigits: 2,
    }).format(amount);
  } catch {
    return amount.toFixed(2);
  }
}

function buildCashflowForecastRows(items, currency) {
  if (!Array.isArray(items)) return [];
  return items.map((item, index) => ({
    id: `${item?.month || "month"}-${item?.lease_ref_code || "lease"}-${index}`,
    month_label: item?.label || "",
    lease_ref_code: item?.lease_ref_code || "-",
    lessee: item?.lessee || "-",
    asset_label: [assetTypeValues[item?.asset_type]?.label, item?.asset_sn].filter(Boolean).join(" ") || "-",
    known_invoiced: formatAmount(item?.known_invoiced, currency),
    projected_rent: formatAmount(item?.projected_rent, currency),
  }));
}

// Clicking a month's bar (either stack) opens the popup with cashflow_forecast_breakdown filtered to that month.
export function useFleetCashflowForecastBreakdown({ forecastBreakdown, currency, onTrack }) {
  const [selectedMonth, setSelectedMonth] = useState(null);
  const reset = useCallback(() => setSelectedMonth(null), []);

  const allRows = useMemo(() => buildCashflowForecastRows(forecastBreakdown, currency), [forecastBreakdown, currency]);
  const rows = useMemo(
    () => (selectedMonth ? allRows.filter((row) => row.month_label === selectedMonth) : []),
    [allRows, selectedMonth],
  );

  const chartEvents = useMemo(() => ({
    click: (params) => {
      if (params?.componentType !== "series") return;
      setSelectedMonth(params.name);
      onTrack?.(`Graph Bar Clicked: ${params.name}`);
    },
  }), [onTrack]);

  return { rows, canRender: allRows.length > 0, chartEvents, modalOpen: !!selectedMonth, closeModal: reset, titleSuffix: selectedMonth || "", reset };
}

export function FleetCashflowForecastBreakdownModal({ open, onClose, rows, titleSuffix }) {
  const title = "12-Month Cash Flow Forecast";
  const rowCount = Array.isArray(rows) ? rows.length : 0;
  const countLabel = `${rowCount} ${rowCount === 1 ? "Lease" : "Leases"}`;
  const subtitle = titleSuffix ? `${titleSuffix}  (${countLabel})` : countLabel;
  return (
    <BreakdownDialog
      open={open}
      onClose={onClose}
      title={title}
      subtitle={subtitle}
      exportFileName={titleSuffix ? `${title} - ${titleSuffix}` : title}
      columns={FLEET_CASHFLOW_FORECAST_BREAKDOWN_COLUMNS}
      rows={rows}
    />
  );
}
